import React, { useEffect, useState } from "react";
import { Typography, Button, Space, Row, Col, Card, Divider, Tag } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";
import notFoundImage from "../../assets/images/not_found.png";
import { mergeDecrypted } from "../../utils/decrypt";

const { Title, Text } = Typography;

const InvoiceDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { request } = useRequest();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  // Get token from localStorage
  const getToken = () => {
    return (
      localStorage.getItem("token") ||
      localStorage.getItem("access_token") ||
      sessionStorage.getItem("token")
    );
  };

  const formatMoney = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    const numeric = Number(value);
    return Number.isFinite(numeric) ? `$${numeric.toFixed(2)}` : `$${value}`;
  };

  const normalizeImageUrl = (url) => {
    if (!url) return "";
    if (Array.isArray(url)) return normalizeImageUrl(url.find(Boolean));
    if (typeof url === "object") {
      return normalizeImageUrl(
        url.url ||
          url.image ||
          url.image_url ||
          url.imageUrl ||
          url.src ||
          url.path,
      );
    }

    const trimmed = String(url).trim();
    if (!trimmed) return "";
    if (
      trimmed.startsWith("http://") ||
      trimmed.startsWith("https://") ||
      trimmed.startsWith("data:") ||
      trimmed.startsWith("blob:")
    )
      return trimmed;
    if (trimmed.startsWith("//")) return window.location.protocol + trimmed;
    return `${apiPath.baseURL.replace(/\/$/, "")}/${trimmed.replace(/^\//, "")}`;
  };

  const normalizeOrderPayload = (data) => {
    // Handle different response structures
    if (data?.data?.order) return data.data.order;
    if (data?.order) return data.order;
    if (data?.data) return data.data;
    return data;
  };

  const getItems = (orderData) => {
    return (
      orderData?.items || orderData?.order_items || orderData?.products || []
    );
  };

  const getItemName = (item) => {
    return (
      item?.name ||
      item?.product_name ||
      item?.title ||
      item?.product?.name ||
      item?.product?.title ||
      "Product"
    );
  };

  const getItemSku = (item) => {
    return (
      item?.sku ||
      item?.product_sku ||
      item?.product_source_id ||
      item?.product?.sku ||
      item?.product?.product_sku ||
      "N/A"
    );
  };

  const getItemQty = (item) => {
    return item?.quantity || item?.qty || item?.count || 1;
  };

  const getItemPrice = (item) => {
    const value =
      item?.unit_price ??
      item?.price ??
      item?.sale_price ??
      item?.subtotal ??
      item?.total_amount ??
      item?.line_total ??
      item?.product?.price;
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : (value ?? "-");
  };

  const getItemImage = (item) => {
    const candidates = [
      item?.image,
      item?.image_url,
      item?.imageUrl,
      item?.product_image,
      item?.thumbnail,
      item?.thumbUrl,
      item?.url,
      item?.product?.image,
      item?.product?.image_url,
      item?.product?.imageUrl,
      item?.product?.thumbnail,
      item?.product?.images,
    ];
    const found = candidates.find((src) =>
      Array.isArray(src)
        ? src.length > 0
        : typeof src === "string"
          ? src.trim() !== ""
          : Boolean(src),
    );
    return normalizeImageUrl(found) || notFoundImage;
  };

  // Get status tag color based on status value
  const getStatusColor = (status) => {
    const statusLower = status?.toLowerCase();
    if (
      statusLower === "delivered" ||
      statusLower === "completed" ||
      statusLower === "paid"
    )
      return "green";
    if (statusLower === "pending" || statusLower === "processing")
      return "orange";
    if (statusLower === "cancelled" || statusLower === "refunded") return "red";
    if (statusLower === "shipped") return "blue";
    return "default";
  };

  useEffect(() => {
    const token = getToken();

    if (!token) {
      console.warn("No token found in localStorage");
      setLoading(false);
      navigate("/login");
      return;
    }

    // Using the same order details API endpoint
    const orderDetailsUrl = apiPath.orderDetails(id);

    request({
      url: orderDetailsUrl,
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      onSuccess: async (data) => {
        const normalizedOrder = normalizeOrderPayload(data);
        let decryptedData = normalizedOrder;

        if (decryptedData.secure) {
          decryptedData = await mergeDecrypted(decryptedData).catch((err) => {
            console.error(
              `Failed to decrypt order id=${decryptedData.id}:`,
              err,
            );
            return decryptedData;
          });
        }

        if (decryptedData.user?.secure) {
          const decryptedUser = await mergeDecrypted(decryptedData.user).catch(
            (err) => {
              console.error(
                `Failed to decrypt user for order id=${decryptedData.id}:`,
                err,
              );
              return decryptedData.user;
            },
          );
          decryptedData = { ...decryptedData, user: decryptedUser };
        }

        setOrder(decryptedData);
        setLoading(false);
      },
      onError: (error) => {
        console.error("Error fetching invoice details:", error);
        if (error?.response?.status === 401) {
          // Token expired or invalid
          localStorage.removeItem("token");
          localStorage.removeItem("access_token");
          sessionStorage.removeItem("token");
          navigate("/login");
        }
        setLoading(false);
      },
    });
  }, [id, request, navigate]);

  if (loading) {
    return (
      <div
        style={{
          padding: "24px",
          textAlign: "center",
          minHeight: "100vh",
          background: "#fff",
        }}
      >
        <Text>Loading invoice details...</Text>
      </div>
    );
  }

  if (!order) {
    return (
      <div
        style={{
          padding: "24px",
          textAlign: "center",
          minHeight: "100vh",
          background: "#fff",
        }}
      >
        <Text>Invoice not found</Text>
        <div style={{ marginTop: "16px" }}>
          <Button type="primary" onClick={() => navigate(-1)}>
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  const items = getItems(order);
  const total =
    order.grand_total ?? order.total ?? order.amount ?? order.total_amount ?? 0;
  const subtotal =
    order.subtotal ??
    order.sub_total ??
    items.reduce((sum, item) => sum + getItemPrice(item) * getItemQty(item), 0);
  const tax = order.tax ?? order.tax_amount ?? 0;
  const shipping =
    order.shipping_cost ?? order.shipping_charge ?? order.delivery_charge ?? 0;
  const discount = order.discount ?? order.discount_amount ?? 0;

  return (
    <div
      className="invoice-details-container"
      style={{ padding: "24px", background: "#fff", minHeight: "100vh" }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
          flexWrap: "wrap",
          gap: "16px",
        }}
      >
        <Title level={4} style={{ margin: 0, fontWeight: 700 }}>
          Invoice #
          {order.invoice_number || order.order_number || order.id || id}
        </Title>
        <Space className="no-print">
          <Button
            type="primary"
            onClick={() => window.print()}
            style={{ background: "#2563eb" }}
          >
            Print Invoice
          </Button>
          <Button
            type="text"
            onClick={() => navigate(-1)}
            style={{ color: "#64748b", fontWeight: 500 }}
          >
            Back
          </Button>
        </Space>
      </div>

      {/* Invoice Header */}
      <Card bordered style={{ marginBottom: "24px", borderRadius: "8px" }}>
        <Row gutter={24}>
          <Col span={12}>
            <div>
              <Title level={5} style={{ marginBottom: "16px" }}>
                Bill To:
              </Title>
              <Text strong>
                {order.customer_name ||
                  order.user?.name ||
                  order.customer?.name ||
                  "N/A"}
              </Text>
              <div style={{ marginTop: "8px" }}>
                <Text style={{ color: "#64748b", display: "block" }}>
                  {order.billing_address_line1 ||
                    order.shipping_address_line1 ||
                    "N/A"}
                </Text>
                <Text style={{ color: "#64748b", display: "block" }}>
                  {order.billing_city || order.shipping_city || "N/A"}
                </Text>
                <Text style={{ color: "#64748b", display: "block" }}>
                  {order.billing_country || order.shipping_country || "N/A"}
                </Text>
                <Text style={{ color: "#64748b", display: "block" }}>
                  {order.customer_email ||
                    order.user?.email ||
                    order.customer?.email ||
                    "N/A"}
                </Text>
              </div>
            </div>
          </Col>
          <Col span={12}>
            <div style={{ textAlign: "right" }}>
              <Title level={5} style={{ marginBottom: "16px" }}>
                Invoice Details:
              </Title>
              <div>
                <Text strong>Invoice Date: </Text>
                <Text style={{ color: "#64748b" }}>
                  {order.created_at
                    ? new Date(order.created_at).toLocaleDateString()
                    : new Date().toLocaleDateString()}
                </Text>
              </div>
              <div style={{ marginTop: "8px" }}>
                <Text strong>Status: </Text>
                <Tag color={getStatusColor(order.status)}>
                  {order.status || "Pending"}
                </Tag>
              </div>
              {order.payment_method && (
                <div style={{ marginTop: "8px" }}>
                  <Text strong>Payment Method: </Text>
                  <Text style={{ color: "#64748b" }}>
                    {order.payment_method}
                  </Text>
                </div>
              )}
            </div>
          </Col>
        </Row>
      </Card>

      <Row gutter={24}>
        <Col xs={24} sm={16} lg={16}>
          <Card
            bordered
            style={{ marginBottom: "24px", borderRadius: "8px" }}
            bodyStyle={{ padding: 0 }}
          >
            <div
              style={{
                padding: "16px 24px",
                borderBottom: "1px solid #f0f0f0",
                display: "flex",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: "10px",
              }}
            >
              <Text strong style={{ color: "#334155" }}>
                Ordered Items ({items.length || 0})
              </Text>
            </div>

            {items.length > 0 ? (
              items.map((item, index) => (
                <div
                  key={`${getItemSku(item)}-${index}`}
                  style={{
                    padding: "24px",
                    display: "flex",
                    justifyContent: "space-between",
                    borderBottom:
                      index !== items.length - 1 ? "1px solid #f0f0f0" : "none",
                    flexWrap: "wrap",
                  }}
                >
                  <Space align="start" size="large" wrap>
                    <div
                      style={{
                        width: 60,
                        height: 60,
                        background: "#f1f5f9",
                        borderRadius: 6,
                        overflow: "hidden",
                        flexShrink: 0,
                      }}
                    >
                      <img
                        src={getItemImage(item)}
                        alt={getItemName(item)}
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = notFoundImage;
                        }}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "4px",
                      }}
                    >
                      <Text strong style={{ color: "#0f172a" }}>
                        {getItemName(item)}
                      </Text>
                      <Text style={{ color: "#64748b", fontSize: "13px" }}>
                        {formatMoney(getItemPrice(item))} Per Unit x{" "}
                        {getItemQty(item)} Quantity
                      </Text>
                      <Text style={{ color: "#64748b", fontSize: "13px" }}>
                        SKU - {getItemSku(item)}
                      </Text>
                    </div>
                  </Space>
                  <div style={{ marginTop: "8px", textAlign: "right" }}>
                    <Text strong>
                      {formatMoney(getItemPrice(item) * getItemQty(item))}
                    </Text>
                  </div>
                </div>
              ))
            ) : (
              <div
                style={{
                  padding: "24px",
                  color: "#64748b",
                  textAlign: "center",
                }}
              >
                No items found for this invoice.
              </div>
            )}

            {/* Invoice Summary */}
            <div
              style={{
                padding: "24px",
                borderTop: "1px solid #f0f0f0",
                background: "#fafafa",
              }}
            >
              <Row justify="end">
                <Col xs={24} sm={16} md={12} lg={10}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: "8px",
                    }}
                  >
                    <Text style={{ color: "#64748b" }}>Subtotal:</Text>
                    <Text>{formatMoney(subtotal)}</Text>
                  </div>
                  {discount > 0 && (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: "8px",
                      }}
                    >
                      <Text style={{ color: "#64748b" }}>Discount:</Text>
                      <Text style={{ color: "#ef4444" }}>
                        -{formatMoney(discount)}
                      </Text>
                    </div>
                  )}
                  {shipping > 0 && (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: "8px",
                      }}
                    >
                      <Text style={{ color: "#64748b" }}>Shipping:</Text>
                      <Text>{formatMoney(shipping)}</Text>
                    </div>
                  )}
                  {tax > 0 && (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: "8px",
                      }}
                    >
                      <Text style={{ color: "#64748b" }}>Tax:</Text>
                      <Text>{formatMoney(tax)}</Text>
                    </div>
                  )}
                  <Divider style={{ margin: "12px 0" }} />
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginTop: "8px",
                    }}
                  >
                    <Text strong style={{ fontSize: "16px" }}>
                      Grand Total:
                    </Text>
                    <Text strong style={{ fontSize: "16px", color: "#2563eb" }}>
                      {formatMoney(total)}
                    </Text>
                  </div>
                </Col>
              </Row>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={8} lg={8}>
          <Card bordered style={{ borderRadius: "8px", marginBottom: "24px" }}>
            <div style={{ marginBottom: "16px" }}>
              <Text strong style={{ color: "#64748b" }}>
                Customer Information
              </Text>
            </div>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "4px" }}
            >
              <Text style={{ color: "#0f172a" }}>
                {order.customer_name ||
                  order.user?.name ||
                  order.customer?.name ||
                  "N/A"}
              </Text>
              <Text style={{ color: "#64748b", fontSize: "13px" }}>
                {order.customer_email ||
                  order.user?.email ||
                  order.customer?.email ||
                  "N/A"}
              </Text>
              {order.customer_phone && (
                <Text style={{ color: "#64748b", fontSize: "13px" }}>
                  {order.customer_phone}
                </Text>
              )}
            </div>

            <Divider style={{ margin: "16px 0" }} />

            <div style={{ marginBottom: "16px" }}>
              <Text strong style={{ color: "#64748b" }}>
                Shipping Address
              </Text>
            </div>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "4px" }}
            >
              <Text strong style={{ color: "#0f172a" }}>
                {order.shipping_name || order.customer_name || "N/A"}
              </Text>
              <Text style={{ color: "#64748b", fontSize: "13px" }}>
                {order.shipping_address_line1 || "N/A"}
              </Text>
              {order.shipping_address_line2 && (
                <Text style={{ color: "#64748b", fontSize: "13px" }}>
                  {order.shipping_address_line2}
                </Text>
              )}
              <Text style={{ color: "#64748b", fontSize: "13px" }}>
                {order.shipping_city || "N/A"}, {order.shipping_state || ""}
              </Text>
              <Text style={{ color: "#64748b", fontSize: "13px" }}>
                {order.shipping_country || "N/A"} {order.shipping_zipcode || ""}
              </Text>
            </div>

            {order.shipping_method && (
              <>
                <Divider style={{ margin: "16px 0" }} />
                <div>
                  <Text strong style={{ color: "#64748b" }}>
                    Shipping Method
                  </Text>
                  <div style={{ marginTop: "8px" }}>
                    <Text>{order.shipping_method}</Text>
                  </div>
                </div>
              </>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default InvoiceDetails;
